"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
// import "swiper/css";
import "swiper/css/pagination";
// import "swiper/css/navigation";

const TestimonialVideos = ({ videos = [] }) => {
  const [activeVideo, setActiveVideo] = useState(null);

  // Check if URL is a YouTube link
  const isYouTubeUrl = (url) => {
    return url?.includes("youtube.com") || url?.includes("youtu.be");
  };

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url?.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Get YouTube thumbnail URL
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeId(url);
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      : null;
  };

  // Get thumbnail for video (YouTube auto-thumbnail or custom thumbnail)
  const getThumbnail = (video) => {
    if (video.thumbnail) {
      return video.thumbnail; // Custom thumbnail provided
    }
    if (isYouTubeUrl(video.url)) {
      return getYouTubeThumbnail(video.url);
    }
    // For S3 videos without thumbnail, return a placeholder or null
    return video.poster || null;
  };

  const handleVideoClick = (index) => {
    setActiveVideo(index);
  };

  const closeModal = () => {
    setActiveVideo(null);
  };

  return (
    <section className="page-container mx-auto py-6 md:py-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl mb-2"
          style={{ fontFamily: "saans-bold" }}
        >
          Hear from Medhavians
        </h2>
        {/* Desktop Navigation Buttons */}
        <div className="hidden md:flex gap-3">
          <button
            className="testimonial-prev-btn cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            className="testimonial-next-btn cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Unified Swiper for Mobile, Tablet, and Desktop */}
      <div className="w-full">
        <Swiper
          modules={[Pagination, Navigation]}
          spaceBetween={20}
          slidesPerView={1}
          breakpoints={{
            // Tablet: 2 slides
            768: {
              slidesPerView: 2,
              spaceBetween: 24,
            },
            // Desktop: 3 slides
            1024: {
              slidesPerView: 3,
              spaceBetween: 32,
            },
          }}
          navigation={{
            prevEl: ".testimonial-prev-btn",
            nextEl: ".testimonial-next-btn",
          }}
          className="testimonial-swiper"
        >
          {videos.map((video, index) => {
            const thumbnail = getThumbnail(video);
            const isYouTube = isYouTubeUrl(video.url);

            return (
              <SwiperSlide key={index}>
                <div
                  className="group relative cursor-pointer overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 mb-5 w-[400px]"
                  onClick={() => handleVideoClick(index)}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-[16/9] bg-gray-200">
                    {thumbnail ? (
                      <Image
                        src={thumbnail}
                        alt={video.name || `Testimonial ${index + 1}`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                      />
                    ) : (
                      <video
                        className="w-full h-full object-cover"
                        src={video.url}
                        preload="metadata"
                      />
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-all duration-300" />

                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <svg
                          className="w-6 h-6 md:w-8 md:h-8 text-[#0475B3] ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Student Name */}
                  {video.name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 z-10">
                      <p className="text-white font-saansMedium text-sm md:text-base">
                        {video.name}
                      </p>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {/* Mobile Navigation Buttons */}
        <div className="flex md:hidden justify-center gap-3 mt-4">
          <button
            className="testimonial-prev-btn cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <button
            className="testimonial-next-btn cursor-pointer bg-white border border-gray-300 rounded-full p-2 shadow hover:bg-gray-100 transition"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={closeModal}
          style={{
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            className="relative w-full max-w-5xl aspect-video"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: "scaleIn 0.3s ease-out",
            }}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              aria-label="Close video"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Video Player - YouTube or HTML5 */}
            {isYouTubeUrl(videos[activeVideo].url) ? (
              // YouTube Embed
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeId(
                  videos[activeVideo].url
                )}?autoplay=1`}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              // S3 / Direct Video
              <video
                className="w-full h-full"
                controls
                autoPlay
                src={videos[activeVideo].url}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>

          {/* Inline Keyframe Animations */}
          <style jsx>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes scaleIn {
              from {
                opacity: 0;
                transform: scale(0.9);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }
          `}</style>
        </div>
      )}
    </section>
  );
};

export default TestimonialVideos;